import React, { createContext, useState, useContext, useEffect } from 'react';
import { getCurrentUser, login as loginApi, logout as logoutApi } from '../services/auth';

// Create the auth context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider component that wraps the app and makes auth object available to any child component that calls useAuth()
export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to refresh the current user data
  const refreshUser = async () => {
    try {
      console.log('Refreshing current user data');
      const userData = await getCurrentUser();

      if (userData && userData.user) {
        console.log('Setting refreshed user data:', userData.user);
        setCurrentUser(userData.user);

        // Also set the profile data if available
        if (userData.profile) {
          console.log('Setting refreshed profile data:', userData.profile);
          setCurrentProfile(userData.profile);
        }

        return userData.user;
      } else {
        console.log('No user data returned or invalid format during refresh');
        return null;
      }
    } catch (err) {
      console.error('Error refreshing user data:', err);
      return null;
    }
  };

  // Check if user is already logged in
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        console.log('Attempting to fetch current user with session cookie');
        // Always try to get the current user - the server will check the session cookie
        const userData = await getCurrentUser();
        console.log('Response from /me:', userData);

        if (userData && userData.user) {
          console.log('Setting current user:', userData.user);
          setCurrentUser(userData.user);

          // Also set the profile data if available
          if (userData.profile) {
            console.log('Setting profile data:', userData.profile);
            setCurrentProfile(userData.profile);
          }
        } else {
          console.log('No user data returned or invalid format');
        }
      } catch (err) {
        console.error('Error fetching current user:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentUser();
  }, []);

  // Login function
  const login = async (credentials) => {
    setError(null);
    try {
      const response = await loginApi(credentials);
      if (response && response.user) {
        setCurrentUser(response.user);

        // Also set the profile data if available
        if (response.profile) {
          setCurrentProfile(response.profile);
        }

        return response.user;
      }
    } catch (err) {
      setError(err.errors || err.message || 'Login failed');
      throw err;
    }
  };

  // Logout function
  const logout = async () => {
    setError(null);
    try {
      await logoutApi();
      setCurrentUser(null);
      setCurrentProfile(null);
    } catch (err) {
      setError(err.message || 'Logout failed');
      throw err;
    }
  };

  // Value object that will be passed to any consumer components
  const value = {
    currentUser,
    currentProfile,
    isAuthenticated: !!currentUser,
    login,
    logout,
    refreshUser,
    error,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
